import { useCallback, useEffect, useState } from 'react';
import type { HistoryItem, AnalysisResult } from '../lib/types';

const STORAGE_KEY = 'tia.history';
const MAX_ITEMS = 12;

function readInitial(): HistoryItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as HistoryItem[];
  } catch {
    return [];
  }
}

export function useHistory(): {
  items: HistoryItem[];
  push: (r: AnalysisResult) => void;
  clear: () => void;
} {
  const [items, setItems] = useState<HistoryItem[]>(readInitial);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const push = useCallback((r: AnalysisResult) => {
    setItems((prev) => {
      const next: HistoryItem = {
        id: `${r.slug}-${Date.now()}`,
        slug: r.slug,
        title: r.title,
        date: r.date,
        verdict: r.verdict,
      };
      const dedup = prev.filter((p) => p.slug !== r.slug);
      return [next, ...dedup].slice(0, MAX_ITEMS);
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return { items, push, clear };
}
